/**
 * Mobility Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/mobility/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to MOBILITY_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { MOBILITY_ELEMENT_COLORS, type MobilityElementId } from '../constants/element-colors';

interface MobilityElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<MobilityElementId, string>>;
}

interface MobilityElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: MobilityElementId) => string;
  /** Set a user override */
  setColor: (id: MobilityElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: MobilityElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type MobilityElementColorsStore = MobilityElementColorsState & MobilityElementColorsActions;

export const useMobilityElementColorsStore = create<MobilityElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: MobilityElementId): string {
          return get().overrides[id] ?? MOBILITY_ELEMENT_COLORS[id];
        },

        setColor(id: MobilityElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: MobilityElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'mobility-element-colors' },
    ),
    { name: 'MobilityElementColors' },
  ),
);
